import * as React from "react";

import { DateLike } from "app/lib/date";
import { isJobFinished } from "app/lib/jobs";
import LegacyDispatcher from "app/lib/legacyDispatcher";

import GitCommitNotFoundWarning from "./GitCommitNotFoundWarning";
import JobLogOutput from "./Output";
import JobLogExitInformation from "./ExitInformation";
import AuthWarning from "./AuthWarning";
import DockerRateLimitedWarning from "./DockerRateLimitedWarning";

import UserSessionStore from "app/stores/UserSessionStore";

import Emojify from "app/components/shared/Emojify";
import ScrollFollower from "app/components/shared/ScrollFollower";
import Icon from "app/components/shared/Icon";

import { Job } from "app/stores/JobLogStore";
import {
  Log,
  LogBody,
  LogControl,
  LogMessage,
} from "app/components/shared/Log";

const USER_SESSION_STORE_KEYS = {
  TIMESTAMPS_ON: "job-log-timestamps-on",
  THEME: "code-theme",
} as const;

type Props = {
  parentElementId: string;
  job: {
    id: string;
    basePath: string;
    state: string;
    passed: boolean;
    logDeletedMessage?: string;
    projectProviderId: string;
    startedAt: DateLike | null | undefined;
    agentOsId: string | null | undefined;
    agentName: string | null | undefined;
    agentVersion: string | null | undefined;
    clusterQueue?: {
      hosted?: boolean;
    };
    permissions: {
      deleteJobLog: {
        allowed: boolean;
      };
    };
  };
  build: {
    branchName: string;
    commitId: string;
  };
};

type State = {
  following: boolean;
  theme: "default" | "solarized";
  sshKeyFailure?: boolean;
  httpsAuthFailure?: boolean;
  dockerRateLimited?: boolean;
  gitCommitNotFoundInBranchFailure?: boolean;
  linesHaveTimestamps?: boolean;
  timestampsOn?: boolean;
};

const getTheme = () => {
  const storedTheme = UserSessionStore.get(USER_SESSION_STORE_KEYS.THEME);
  return storedTheme === "default" || storedTheme === "solarized"
    ? storedTheme
    : "default";
};

export default class JobLog extends React.PureComponent<Props, State> {
  // @ts-expect-error - TS2416 - Property 'state' in type 'JobLog' is not assignable to the same property in base type 'PureComponent<Props, State, any>'.
  state = {
    following: false,
    theme: getTheme(),
  };

  componentDidMount() {
    // If we've got multiple JobLogs open and we change the theme in one, then we should
    // make sure that all instances also switch theme. Fortunately the UserSessionStore
    // has our back, emitting an event when cached state is changed.
    UserSessionStore.on("change", (event) => {
      if (event.key === USER_SESSION_STORE_KEYS.THEME) {
        this.setState({ theme: event.newValue });
      }
    });
  }

  isTimestampsOn() {
    // If timestamps aren't even available in this log, don't bother doing anythingf else
    // @ts-expect-error - TS2339 - Property 'linesHaveTimestamps' does not exist on type '{ following: boolean; theme: string; }'.
    if (!this.state.linesHaveTimestamps) {
      return false;
    }

    // If timestmaps haven't been manually turned on in this log, use what ever
    // they chose last.
    // @ts-expect-error - TS2339 - Property 'timestampsOn' does not exist on type '{ following: boolean; theme: string; }'.
    if (this.state.timestampsOn === undefined) {
      // Values are stored as strings, so we'll need to convert it
      return (
        UserSessionStore.get(USER_SESSION_STORE_KEYS.TIMESTAMPS_ON) === "true"
      );
    }

    // @ts-expect-error - TS2339 - Property 'timestampsOn' does not exist on type '{ following: boolean; theme: string; }'.
    return this.state.timestampsOn;
  }

  render() {
    if (this.props.job.logDeletedMessage) {
      return this.renderDeletedLog();
    }
    return this.renderLog();
  }

  renderDeletedLog() {
    return (
      <Log className="min-h-0 p-0" theme={this.state.theme}>
        <LogBody>
          <LogMessage>
            <Emojify text={this.props.job.logDeletedMessage} />
          </LogMessage>
        </LogBody>
      </Log>
    );
  }

  renderLog() {
    // @ts-expect-error - TS2339 - Property 'sshKeyFailure' does not exist on type '{ following: boolean; theme: string; }'.
    const authFailureNode = (this.state.sshKeyFailure ||
      // @ts-expect-error - TS2339 - Property 'httpsAuthFailure' does not exist on type '{ following: boolean; theme: string; }'.
      this.state.httpsAuthFailure) && (
      <AuthWarning
        repositoryProviderId={this.props.job.projectProviderId}
        osId={this.props.job.agentOsId}
        hostedQueue={this.props.job.clusterQueue?.hosted}
      />
    );

    // @ts-expect-error - TS2339 - Property 'dockerRateLimited' does not exist on type '{ following: boolean; theme: string; }'.
    const dockerRateLimitedNode = this.state.dockerRateLimited && (
      <DockerRateLimitedWarning />
    );

    const gitCommitNotFoundInBranchFailureNode =
      // @ts-expect-error - TS2339 - Property 'gitCommitNotFoundInBranchFailure' does not exist on type '{ following: boolean; theme: string; }'.
      this.state.gitCommitNotFoundInBranchFailure && (
        <GitCommitNotFoundWarning
          branch={this.props.build.branchName}
          commit={this.props.build.commitId}
        />
      );

    const followLogButtonNode = isJobFinished(this.props.job) ? (
      <LogControl onClick={this.handleClickFollowLog}>
        <i className="fa fa-arrow-down" />
        Jump to end
      </LogControl>
    ) : this.state.following ? (
      <LogControl onClick={this.handleClickFollowLog} active={true}>
        <i className="fa fa-arrow-down" />
        Following…
      </LogControl>
    ) : (
      <LogControl onClick={this.handleClickFollowLog}>
        <i className="fa fa-arrow-down" />
        Follow
      </LogControl>
    );

    const isTimestampsOn = this.isTimestampsOn();

    return (
      <div data-testid="JobLogComponent" className="JobLogComponent">
        {authFailureNode}
        {dockerRateLimitedNode}
        {gitCommitNotFoundInBranchFailureNode}
        <ScrollFollower
          following={this.state.following}
          onManualScroll={this.handleManualScroll}
          el={this.props.parentElementId}
        >
          <JobLogOutput
            job={this.props.job}
            onChange={this.handleJobLogChange}
            timestampsOn={isTimestampsOn}
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type '"default" | "solarized"'.
            theme={this.state.theme}
          >
            <LogControl onClick={this.handleClickExpandGroups}>
              <i className="fa fa-plus" />
              Expand groups
            </LogControl>

            <LogControl onClick={this.handleClickCollapseLogs}>
              <i className="fa fa-minus" />
              Collapse groups
            </LogControl>

            {/* @ts-expect-error - TS2339 - Property 'linesHaveTimestamps' does not exist on type '{ following: boolean; theme: string; }'. */}
            {this.state.linesHaveTimestamps && (
              <LogControl onClick={this.handleClickToggleTimestamps}>
                <i className="fa fa-calendar-o" />
                {isTimestampsOn ? "Hide timestamps" : "Show timestamps"}
              </LogControl>
            )}

            <LogControl className="lg:ml-auto" onClick={this.handleThemeToggle}>
              <Icon
                icon={
                  this.state.theme === "default"
                    ? "theme-toggle-off"
                    : "theme-toggle-on"
                }
                className="h-3 w-3"
              />
              Theme
            </LogControl>

            {(this.props.job.state === "finished" ||
              this.props.job.state === "canceled") &&
              this.props.job.permissions.deleteJobLog.allowed && (
                <LogControl
                  href={this.props.job.basePath + "/delete_log"}
                  data-method="delete"
                  data-confirm="Permanently delete this job’s log?"
                >
                  <i className="fa fa-trash" />
                  Delete
                </LogControl>
              )}

            <LogControl href={this.props.job.basePath + "/download.txt"}>
              <i className="fa fa-download" />
              Download
            </LogControl>

            {!window.Features.StreamS3Logs && (
              <LogControl
                href={this.props.job.basePath + "/raw_log"}
                target="_blank"
              >
                <i className="fa fa-file-o" />
                Raw
              </LogControl>
            )}

            {window.Features.OpenRawLogs && (
              <LogControl
                href={this.props.job.basePath + "/log"}
                target="_blank"
              >
                <i className="fa fa-external-link" />
                Open
              </LogControl>
            )}

            {followLogButtonNode}
          </JobLogOutput>

          <div className="JobLogComponent__Footer pt2" style={{ fontSize: 13 }}>
            <JobLogExitInformation
              // @ts-expect-error - TS2769 - No overload matches this call.
              className="JobLogComponent__ExitStatus"
              job={this.props.job}
            />
            <div className="JobLogComponent__BackToTop">
              <a
                href={`#${this.props.parentElementId}`}
                className="JobLogComponent__BackToTop__Button"
              >
                <i className="fa fa-arrow-up" />
                Back to Job
              </a>
            </div>
          </div>
        </ScrollFollower>
      </div>
    );
  }

  handleJobLogChange = (log: Job) => {
    this.setState({
      sshKeyFailure:
        log.sshKeyFailure &&
        this.props.job.state === "finished" &&
        !this.props.job.passed,
      httpsAuthFailure:
        log.httpsAuthFailure &&
        this.props.job.state === "finished" &&
        !this.props.job.passed,
      dockerRateLimited:
        log.dockerRateLimited &&
        this.props.job.state === "finished" &&
        !this.props.job.passed,
      gitCommitNotFoundInBranchFailure:
        log.gitCommitNotFoundInBranchFailure &&
        this.props.job.state === "finished" &&
        !this.props.job.passed,
      linesHaveTimestamps: log.linesHaveTimestamps,
    });
  };

  handleManualScroll = () => {
    this.setState({ following: false });
  };

  handleThemeToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const currentTheme = getTheme();
    const newTheme = currentTheme === "default" ? "solarized" : "default";

    this.setState({ theme: newTheme });
    UserSessionStore.set(USER_SESSION_STORE_KEYS.THEME, newTheme);
  };

  handleClickFollowLog = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    this.setState({ following: !this.state.following });
  };

  handleClickExpandGroups = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    LegacyDispatcher.emit("job_log_group:expand_all", {
      jobId: this.props.job.id,
    });
  };

  handleClickCollapseLogs = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    LegacyDispatcher.emit("job_log_group:collapse_all", {
      jobId: this.props.job.id,
    });
  };

  handleClickToggleTimestamps = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    const timestampsOn = !this.isTimestampsOn();

    this.setState({ timestampsOn: timestampsOn });

    // Store it so next time we default to that choice
    UserSessionStore.set(
      USER_SESSION_STORE_KEYS.TIMESTAMPS_ON,
      timestampsOn.toString(),
    );
  };
}
